// Dashboard.js
import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import ProfilePicture from './ProfilePicture';
import AuthModal from './AuthModal';
import { Target, LogOut, User, Menu, ChevronLeft, Download, X, Check } from 'lucide-react';
import DownloadDialog from './DownloadDialog';
import { deliverFiles } from './lib/deliverFiles';

function Dashboard() {
  const [downloadRequest, setDownloadRequest] = useState(null);
  const [savedImages, setSavedImages] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isPreviewLocked, setIsPreviewLocked] = useState(false);
  const [showNewBoardForm, setShowNewBoardForm] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [activeBoard, setActiveBoard] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [draggingImage, setDraggingImage] = useState(null);
  const [draggingBoard, setDraggingBoard] = useState(null);
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editingBoardName, setEditingBoardName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(true);
  // New state for showing recycle bin
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  // New state for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState(null);
  // Mobile image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  // Mobile boards modal state
  const [showBoardsModal, setShowBoardsModal] = useState(false);
  // Mobile save-to-photos state
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  
  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setUser(userData);
        
        // Fetch full user data including profile picture
        const fetchUserData = async () => {
          try {
            const response = await fetch('/api/users/me', {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (response.ok) {
              const fullUserData = await response.json();
              setUser(fullUserData);
              // Update local storage with full user data
              localStorage.setItem('user', JSON.stringify(fullUserData));
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        };
        
        fetchUserData();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        handleLogout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Click outside: unlock and clear preview
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.grid-item') &&
          !event.target.closest('.preview-container')) {
        setIsPreviewLocked(false);
        setPreviewImage(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch boards when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchBoards();
    }
  }, [isAuthenticated]);
  
  // Fetch images when activeBoard changes
  useEffect(() => {
    if (isAuthenticated && activeBoard) {
      fetchSavedImages(activeBoard);
    }
  }, [isAuthenticated, activeBoard]);

  const fetchSavedImages = async (boardId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Special case for "all" board - don't filter by boardId
      const url = boardId === 'all'
        ? '/api/saved-images/all'
        : `/api/saved-images?boardId=${boardId}`;
      
      const response = await fetch(url, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      setSavedImages(data);
      setError(null);
    } catch (err) {
      console.error('Fetch images error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Update the fetchBoards function in Dashboard.js to properly count all images

const fetchBoards = async () => {
  try {
    const token = localStorage.getItem('token');
    
    // First, get all boards
    const boardsResponse = await fetch('/api/boards', {
      headers: {
        'x-auth-token': token
      }
    });
    
    if (!boardsResponse.ok) {
      throw new Error(`Server error: ${boardsResponse.status}`);
    }
    
    let boardsData = await boardsResponse.json();
    
    // Next, get the total count of all saved images for this user
    const countResponse = await fetch('/api/saved-images/count', {
      headers: {
        'x-auth-token': token
      }
    });
    
    if (!countResponse.ok) {
      throw new Error(`Server error: ${countResponse.status}`);
    }
    
    const countData = await countResponse.json();
    const totalImageCount = countData.count;
    
    // Create virtual "All" board with the accurate total count
    const allBoard = {
      id: 'all',
      name: 'All',
      _count: {
        savedImages: totalImageCount
      },
      isVirtual: true
    };
    
    // Add the "All" board at the beginning
    boardsData = [allBoard, ...boardsData];
    setBoards(boardsData);
    
    // Set active board to "All" if none is selected
    if (!activeBoard) {
      setActiveBoard('all');
    }
  } catch (err) {
    console.error('Fetch boards error:', err);
  }
};

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  // Hover: only update preview when NOT locked
  const handleImageHover = (image) => {
    if (!isPreviewLocked) {
      setPreviewImage(image);
    }
  };

  // Check if we're on mobile
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Single click: lock preview to this image (or switch lock to new image)
  // Cmd+click: select/deselect for multi-download
  const handleImageClick = (image, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Cmd+click (Mac) or Ctrl+click (Windows) to select/deselect
    if (event.metaKey || event.ctrlKey) {
      // If entering select mode and there's a locked preview, include it too
      if (!isSelectMode && isPreviewLocked && previewImage) {
        setSelectedImages(prev => {
          const next = new Set(prev);
          next.add(previewImage.id);
          next.add(image.id);
          return next;
        });
      } else {
        toggleImageSelection(image.id);
      }
      if (!isSelectMode) setIsSelectMode(true);
      return;
    }

    if (isMobile()) {
      setModalImage(image);
      setShowImageModal(true);
    } else {
      // On desktop: lock preview to this image
      setPreviewImage(image);
      setIsPreviewLocked(true);
    }
  };

  // Double-click: toggle selection
  const handleImageDoubleClick = (image, event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleImageSelection(image.id);
    if (!isSelectMode) setIsSelectMode(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImage(null);
  };

  const openBoardsModal = () => {
    setShowBoardsModal(true);
  };

  const closeBoardsModal = () => {
    setShowBoardsModal(false);
  };
  
  const toggleNewBoardForm = () => {
    setShowNewBoardForm(!showNewBoardForm);
    setNewBoardName('');
  };
  
  const handleNewBoardSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/boards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          name: newBoardName,
          description: ''
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Refresh the boards list
      await fetchBoards();
      toggleNewBoardForm();
      
      // Set the new board as active
      setActiveBoard(data.board.id);
      
      showNotification('Board created successfully');
      
    } catch (err) {
      console.error('Create board error:', err);
      setError(err.message);
    }
  };
  
  const handleBoardClick = (boardId) => {
    setActiveBoard(boardId);
    // Reset preview image when changing boards
    setPreviewImage(null);
    setIsPreviewLocked(false);
  };
  
  // Image drag and drop handlers
  const handleImageDragStart = (image, e) => {
    // Set the data to be transferred
    e.dataTransfer.setData('type', 'image');
    e.dataTransfer.setData('id', image.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Set state to track which image is being dragged
    setDraggingImage(image);
    
    // Show recycle bin when dragging starts
    setShowRecycleBin(true);
  };
  
  // Board drag and drop handlers
  const handleBoardDragStart = (board, e) => {
    // Don't allow dragging the "All" board
    if (board.id === 'all' || board.isVirtual) {
      e.preventDefault();
      return;
    }
    
    // Set the data to be transferred
    e.dataTransfer.setData('type', 'board');
    e.dataTransfer.setData('id', board.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Set state to track which board is being dragged
    setDraggingBoard(board);
    
    // Show recycle bin when dragging starts
    setShowRecycleBin(true);
  };
  
  const handleDragEnd = () => {
    // Reset dragging state
    setDraggingImage(null);
    setDraggingBoard(null);
    
    // Hide recycle bin when dragging ends
    setShowRecycleBin(false);
  };
  
  const handleDragOver = (e, boardId) => {
    // Prevent default to allow drop
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add highlight when dragging over (except for "All" board)
    if (boardId && boardId !== 'all') {
      const boardElement = e.currentTarget.closest('.board-item') || e.currentTarget;
      if (boardElement.classList.contains('board-item')) {
        boardElement.classList.add('drag-over');
      }
    }
  };
  
  const handleDragLeave = (e) => {
    // Remove highlight when leaving the board area
    const boardElement = e.currentTarget.closest('.board-item') || e.currentTarget;
    if (!boardElement.contains(e.relatedTarget)) {
      boardElement.classList.remove('drag-over');
    }
  };
  
  const handleDrop = async (boardId, e) => {
    e.preventDefault();
    
    // Remove highlight class
    e.currentTarget.classList.remove('drag-over');
    
    const type = e.dataTransfer.getData('type');
    
    // Only handle image drops here
    if (type !== 'image') {
      return;
    }
    
    // Don't allow dropping on the "All" board
    if (boardId === 'all') {
      showNotification('Cannot move images to the "All" board');
      return;
    }
    
    const imageId = e.dataTransfer.getData('id');
    
    // Don't do anything if dropped on the same board
    if (boardId === activeBoard) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/saved-images/${imageId}/board`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          boardId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // If we're in a specific board view, remove the image
      if (activeBoard !== 'all') {
        setSavedImages(savedImages.filter(img => img.id !== imageId));
      }
      
      // Refresh the boards list to update counts
      await fetchBoards();
      
      // Show confirmation notification
      showNotification('Image moved to board successfully');
      
    } catch (err) {
      console.error('Move image error:', err);
      showNotification('Error moving image: ' + err.message);
    }
  };

  // Recycle bin drag handlers
  const handleRecycleBinDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.classList.add('recycle-bin-drag-over');
  };
  
  const handleRecycleBinDragLeave = (e) => {
    e.currentTarget.classList.remove('recycle-bin-drag-over');
  };
  
  // Handler for dropping on recycle bin
  const handleRecycleBinDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('recycle-bin-drag-over');
    
    const type = e.dataTransfer.getData('type');
    const id = e.dataTransfer.getData('id');
    
    if (type === 'image') {
      // Handle image deletion (no confirmation)
      deleteImage(id);
    } else if (type === 'board') {
      // Find the board to delete
      const board = boards.find(b => b.id === id);
      
      if (board) {
        // Set the board to delete and show confirmation modal
        setBoardToDelete(board);
        setShowConfirmModal(true);
      }
    }
  };
  
  // Function to delete an image
  const deleteImage = async (imageId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/saved-images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      // Remove the image from the current view
      setSavedImages(savedImages.filter(img => img.id !== imageId));
      
      // If preview image is the deleted one, clear it
      if (previewImage && previewImage.id === imageId) {
        setPreviewImage(null);
        setIsPreviewLocked(false);
      }
      
      // Refresh the boards list to update counts
      await fetchBoards();
      
      // Show confirmation notification
      showNotification('Image deleted successfully');
      
    } catch (err) {
      console.error('Delete image error:', err);
      showNotification('Error deleting image: ' + err.message);
    }
  };
  
  // Function to delete a board
  const deleteBoard = async () => {
    if (!boardToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/boards/${boardToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      // If the active board is being deleted, switch to "All"
      if (activeBoard === boardToDelete.id) {
        setActiveBoard('all');
      }
      
      // Refresh the boards list
      await fetchBoards();
      
      // Show confirmation notification
      showNotification('Board deleted successfully');
      
    } catch (err) {
      console.error('Delete board error:', err);
      showNotification('Error deleting board: ' + err.message);
    } finally {
      // Close the confirmation modal
      setShowConfirmModal(false);
      setBoardToDelete(null);
    }
  };
  
  // Function to cancel board deletion
  const cancelDeleteBoard = () => {
    setShowConfirmModal(false);
    setBoardToDelete(null);
  };
  
  // Board renaming handlers
  const handleBoardDoubleClick = (board, e) => {
    e.stopPropagation();
    
    // Don't allow renaming the "All" board
    if (board.id === 'all') {
      showNotification('The "All" board cannot be renamed');
      return;
    }
    
    setEditingBoardId(board.id);
    setEditingBoardName(board.name);
  };
  
  const handleBoardNameChange = (e) => {
    // Limit to 30 characters
    if (e.target.value.length <= 30) {
      setEditingBoardName(e.target.value);
    }
  };
  
  const handleBoardRenameSubmit = async (e) => {
    e.preventDefault();
    
    console.log("Starting board rename process");
    console.log("Board being renamed:", editingBoardId);
    
    // Trim whitespace
    const trimmedName = editingBoardName.trim();
    console.log("New name:", trimmedName);
    
    // Validate name
    if (!trimmedName) {
      console.log("Error: Empty board name");
      showNotification('Board name cannot be empty');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log("Token retrieved:", token ? "Yes" : "No");
      
      console.log("Sending PATCH request to:", `/api/boards/${editingBoardId}`);
      
      const response = await fetch(`/api/boards/${editingBoardId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          name: trimmedName
        })
      });
      
      console.log("Response status:", response.status);
      
      const data = await response.json();
      console.log("Response data:", data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Error renaming board');
      }
      
      // Reset editing state
      setEditingBoardId(null);
      setEditingBoardName('');
      
      // Refresh boards
      console.log("Refreshing boards after rename");
      await fetchBoards();
      
      showNotification('Board renamed successfully');
      console.log("Rename successful, notification shown");
    } catch (err) {
      console.error('Rename board error:', err);
      showNotification(`Error renaming board: ${err.message}`);
    }
  };
  
  const handleCancelRename = () => {
    setEditingBoardId(null);
    setEditingBoardName('');
  };
  
  const handleRenameKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelRename();
    }
  };
  
  const showNotification = (message) => {
    setNotification({ show: true, message });
    setTimeout(() => {
      setNotification({ show: false, message: '' });
    }, 2000);
  };

  // Archive.org answers 200 with its own logo for items that have no image, so
  // a notfound URL is a known-dead entry rather than something worth fetching.
  const isDownloadable = (image) => {
    const url = image.fullSizeUrl || image.thumbnailUrl;
    return !!url && url !== 'https://archive.org/images/notfound.png';
  };

  const handleDownloadBoard = () => {
    if (!activeBoard) {
      showNotification('Please select a board to download');
      return;
    }

    const downloadable = savedImages.filter(isDownloadable);
    if (downloadable.length === 0) {
      showNotification('No images to download');
      return;
    }

    const boardName = activeBoard === 'all' ? 'all_boards' :
      (boards.find(b => b.id === activeBoard)?.name || 'board');

    setDownloadRequest({
      images: downloadable,
      zipName: `${boardName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_images.zip`,
    });
  };

  // Mobile: toggle image selection
  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  // Save a single image to photos via share sheet or download
  const handleSaveToPhotos = (image) => {
    if (!isDownloadable(image)) {
      showNotification('No image available to save');
      return;
    }
    setDownloadRequest({ images: [image], zipName: 'image.zip' });
  };

  // Save selected or all images to photos
  const handleMobileSave = (saveAll) => {
    const imagesToSave = (saveAll
      ? savedImages
      : savedImages.filter(img => selectedImages.has(img.id))
    ).filter(isDownloadable);

    if (imagesToSave.length === 0) {
      showNotification(saveAll ? 'No images to save' : 'No images selected');
      return;
    }

    setShowSaveMenu(false);
    setIsSelectMode(false);
    setSelectedImages(new Set());
    setDownloadRequest({ images: imagesToSave, zipName: 'images.zip' });
  };

  const runDownload = async (files, setPercent, { failed = 0 } = {}) => {
    const outcome = await deliverFiles(files, {
      zipName: downloadRequest.zipName,
      onZipPercent: setPercent,
    });
    if (outcome === 'cancelled') return;
    // Delivering 10 of 50 files under a bare "Saved 10 images" hides the other
    // 40 entirely, which is what the pre-dialog code was careful not to do.
    const saved = `Saved ${files.length} image${files.length === 1 ? '' : 's'}`;
    showNotification(failed > 0 ? `${saved} (${failed} failed)` : saved);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleSidebar = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  if (!isAuthenticated && !loading) {
    return (
      <div className="dashboard-container">
        <div className="not-authenticated">
          <h2>Please log in to view your saved images</h2>
          <button className="return-button" onClick={() => window.location.href = '/'}>
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading && boards.length === 0) {
    return <div className="dashboard-loading">Loading your saved images...</div>;
  }

  if (error) {
    return <div className="dashboard-error">Error: {error}</div>;
  }

  return (
    <div className="page-container">
      {notification.show && (
        <div className="notification">
          {notification.message}
        </div>
      )}
      
      {/* Sidebar with minimize functionality */}
      <div className={`sidebar ${isSidebarMinimized ? 'minimized' : ''}`}>
        {!isSidebarMinimized ? (
          <div className="sidebar-nav">
            <button className="nav-button minimize-button" onClick={toggleSidebar} title="Minimize sidebar">
              <ChevronLeft size={18} />
            </button>
            {isAuthenticated ? (
              <>
                <a href="/" className="nav-button" title="Home"><Target size={18} /></a>
                <a href={user ? `/${user.username}` : '/dashboard'} className="nav-button active" title="Profile"><User size={18} /></a>
                <button className="nav-button" onClick={handleLogout} title="Logout"><LogOut size={18} /></button>
              </>
            ) : (
              <button className="nav-button" onClick={toggleModal} title="Login"><User size={18} /></button>
            )}
          </div>
        ) : (
          <button className="nav-button expand-button" onClick={toggleSidebar} title="Expand sidebar">
            <Menu size={18} />
          </button>
        )}
      </div>

      {/* Dashboard Container */}
      <div className="dashboard-container">
        <div className="dashboard-content">
        {/* Left Container - Preview */}
        <div className="preview-container">
          {previewImage ? (
            <div className="preview-content">
              <div className="preview-image-wrapper">
                <img 
                  src={previewImage.fullSizeUrl || previewImage.thumbnailUrl}
                  alt=""
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = previewImage.thumbnailUrl || "https://archive.org/images/notfound.png";
                  }}
                />
              </div>
              <div className="preview-metadata">
                <h3>{previewImage.title}</h3>
                {previewImage.description && <p>{previewImage.description}</p>}
                <div className="preview-actions">
                  <a
                    href={previewImage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-button"
                  >
                    View source
                  </a>
                  <button
                    className="save-photo-button"
                    onClick={() => handleSaveToPhotos(previewImage)}
                  >
                    Save or Share
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Hover over an image to preview<br />Click to lock preview</p>
            </div>
          )}
        </div>
        
        {/* Middle Container - Image Grid */}
        <div className="image-grid-container">
          {loading ? (
            <div className="loading-indicator">Loading images...</div>
          ) : savedImages.length === 0 ? (
            <div className="no-images-message">
              <p>No images in this board yet</p>
            </div>
          ) : (
            <div className="image-grid">
              {savedImages.map((image) => (
                <div
                  key={image.id}
                  className={`grid-item ${previewImage && previewImage.id === image.id && isPreviewLocked ? 'active' : ''} ${isSelectMode && selectedImages.has(image.id) ? 'selected' : ''}`}
                  onMouseEnter={() => handleImageHover(image)}
                  onClick={(e) => {
                    if (isSelectMode) {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleImageSelection(image.id);
                    } else {
                      handleImageClick(image, e);
                    }
                  }}
                  onDoubleClick={(e) => handleImageDoubleClick(image, e)}
                  draggable={!isSelectMode}
                  onDragStart={(e) => handleImageDragStart(image, e)}
                  onDragEnd={handleDragEnd}
                >
                  <img
                    src={image.thumbnailUrl}
                    alt=""
                    onError={(e) => {
                      const img = e.target;
                      if (!img.src.includes('/api/proxy-image')) {
                        img.src = `/api/proxy-image?url=${encodeURIComponent(image.thumbnailUrl)}`;
                      } else {
                        img.onerror = null;
                        img.src = "https://archive.org/images/notfound.png";
                      }
                    }}
                  />
                  {isSelectMode && selectedImages.has(image.id) && (
                    <div className="select-check">
                      <Check size={16} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Right Container - Boards */}
        <div className="boards-container">
          <div className="boards-header">
            <h2>Your Boards</h2>
            <div className="boards-actions">
              {activeBoard && savedImages.length > 0 && (
                <button 
                  className="download-board-button" 
                  onClick={handleDownloadBoard}
                  title={activeBoard === 'all' ? "Download all images from all boards" : "Download all images from this board"}
                >
                  <Download size={16} />
                </button>
              )}
              <button 
                className="add-board-button" 
                onClick={toggleNewBoardForm}
                title="Create new board"
              >
                +
              </button>
            </div>
          </div>
          
          {showNewBoardForm ? (
            <form className="new-board-form" onSubmit={handleNewBoardSubmit}>
              <input
                type="text"
                placeholder="Enter board name"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                required
                maxLength={30}
              />
              <div className="form-actions">
                <button type="button" onClick={toggleNewBoardForm}>Cancel</button>
                <button type="submit">Create</button>
              </div>
            </form>
          ) : null}
          
          <div className="board-list">
            {boards.map(board => (
              <div 
                key={board.id} 
                className={`board-item ${activeBoard === board.id ? 'active' : ''} ${board.id === 'all' ? 'all-board' : ''}`}
                onClick={() => handleBoardClick(board.id)}
                onDoubleClick={(e) => handleBoardDoubleClick(board, e)}
                onDragOver={board.id !== 'all' ? (e) => handleDragOver(e, board.id) : null}
                onDragLeave={board.id !== 'all' ? handleDragLeave : null}
                onDrop={(e) => handleDrop(board.id, e)}
                draggable={board.id !== 'all'}
                onDragStart={(e) => handleBoardDragStart(board, e)}
                onDragEnd={handleDragEnd}
              >
                {editingBoardId === board.id ? (
                  <form className="rename-form" onSubmit={handleBoardRenameSubmit} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editingBoardName}
                      onChange={handleBoardNameChange}
                      onKeyDown={handleRenameKeyDown}
                      autoFocus
                      onBlur={handleCancelRename}
                      onClick={(e) => e.stopPropagation()}
                      maxLength={30}
                    />
                    <div className="rename-actions">
                      
                    </div>
                  </form>
                ) : (
                  <>
                    <span>{board.name}</span>
                    <span className="image-count">
                      {board._count.savedImages} {board._count.savedImages === 1 ? 'image' : 'images'}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Recycle Bin */}
          {showRecycleBin && (
            <div className="recycle-bin">
              <div className="recycle-bin-label">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                <span>Drop to delete</span>
              </div>
              <div 
                className="recycle-bin-hole"
                onDragOver={handleRecycleBinDragOver}
                onDragLeave={handleRecycleBinDragLeave}
                onDrop={handleRecycleBinDrop}
              >
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
      
      {/* Mobile Boards Button */}
      <button className="mobile-boards-button" onClick={openBoardsModal}>
        Boards
      </button>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showModal} 
        onClose={async (success, userData) => {
          setShowModal(false);
          if (success && userData) {
            setIsAuthenticated(true);
            setUser(userData);
            // Save any pending session images from before account creation
            const pending = localStorage.getItem('pendingSessionImages');
            if (pending) {
              try {
                const pendingImages = JSON.parse(pending);
                const token = localStorage.getItem('token');
                await Promise.all(pendingImages.map(item =>
                  fetch('/api/saved-images', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                    body: JSON.stringify({
                      archiveId: item.id,
                      title: item.name,
                      description: item.description,
                      url: item.url,
                      thumbnailUrl: item.thumbnailUrl,
                      fullSizeUrl: item.fullSizeUrl || null,
                    }),
                  }).catch(() => {})
                ));
              } catch (e) {
                console.error('Error saving pending images:', e);
              }
              localStorage.removeItem('pendingSessionImages');
            }
          }
        }}
      />
      
      {/* Confirmation Modal for Board Deletion */}
      {showConfirmModal && boardToDelete && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <p>Are you sure you want to delete this board and all its contents?</p>
            <div className="confirm-actions">
              <button 
                className="cancel-button" 
                onClick={cancelDeleteBoard}
              >
                Cancel
              </button>
              <button 
                className="delete-button" 
                onClick={deleteBoard}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Image Modal for Mobile */}
      {showImageModal && modalImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              <X size={16} />
            </button>
            <img 
              src={modalImage.fullSizeUrl || modalImage.thumbnailUrl}
              alt={modalImage.title || 'Image'}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://archive.org/images/notfound.png";
              }} 
            />
            <div className="image-modal-info">
              <h3>{modalImage.title || 'Untitled'}</h3>
              {modalImage.description && <p>{modalImage.description}</p>}
              <a
                href={modalImage.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View source
              </a>
              <button
                className="modal-save-photo-button"
                onClick={() => handleSaveToPhotos(modalImage)}
              >
                Save or Share
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Boards Modal for Mobile */}
      {showBoardsModal && (
        <div className="boards-modal-overlay" onClick={closeBoardsModal}>
          <div className="boards-modal" onClick={(e) => e.stopPropagation()}>
            <div className="boards-modal-header">
              <h3>Your Boards</h3>
              <button className="modal-close-button" onClick={closeBoardsModal}>
                <X size={20} />
              </button>
            </div>
            <div className="boards-modal-content">
              <div className="boards-header">
                <div className="boards-actions">
                  {activeBoard && savedImages.length > 0 && (
                    <button 
                      className="download-board-button" 
                      onClick={handleDownloadBoard}
                      title={activeBoard === 'all' ? "Download all images from all boards" : "Download all images from this board"}
                    >
                      <Download size={16} />
                    </button>
                  )}
                  <button 
                    className="add-board-button" 
                    onClick={toggleNewBoardForm}
                    title="Create new board"
                  >
                    +
                  </button>
                </div>
              </div>
              
              {showNewBoardForm ? (
                <form className="new-board-form" onSubmit={handleNewBoardSubmit}>
                  <input
                    type="text"
                    placeholder="Enter board name"
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    required
                    maxLength={30}
                  />
                  <div className="form-actions">
                    <button type="button" onClick={toggleNewBoardForm}>Cancel</button>
                    <button type="submit">Create</button>
                  </div>
                </form>
              ) : null}
              
              <div className="board-list">
                {boards.map(board => (
                  <div 
                    key={board.id} 
                    className={`board-item ${activeBoard === board.id ? 'active' : ''} ${board.id === 'all' ? 'all-board' : ''}`}
                    onClick={() => handleBoardClick(board.id)}
                    onDoubleClick={(e) => handleBoardDoubleClick(board, e)}
                  >
                    {editingBoardId === board.id ? (
                      <form className="rename-form" onSubmit={handleBoardRenameSubmit} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editingBoardName}
                          onChange={(e) => setEditingBoardName(e.target.value)}
                          onBlur={handleCancelRename}
                          onKeyDown={handleRenameKeyDown}
                          autoFocus
                          maxLength={30}
                        />
                      </form>
                    ) : (
                      <>
                        <span>{board.name}</span>
                        <span className="image-count">
                          {board._count.savedImages} {board._count.savedImages === 1 ? 'image' : 'images'}
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop selection bar — shows when cmd+clicking images */}
      {isSelectMode && selectedImages.size > 0 && !isMobile() && (
        <div className="desktop-select-bar">
          <span className="select-count">{selectedImages.size} selected</span>
          <button className="mobile-save-btn" onClick={() => handleMobileSave(false)}>
            Download Selected
          </button>
          <button
            className="mobile-save-btn cancel"
            onClick={() => { setIsSelectMode(false); setSelectedImages(new Set()); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Mobile Save to Photos bar — hide when image modal is open */}
      {isAuthenticated && !showImageModal && (
        <div className="mobile-save-bar">
          {isSelectMode ? (
            <>
              <span className="select-count">{selectedImages.size} selected</span>
              <button
                className="mobile-save-btn"
                onClick={() => handleMobileSave(false)}
                disabled={selectedImages.size === 0}
              >
                Save Selected
              </button>
              <button
                className="mobile-save-btn cancel"
                onClick={() => { setIsSelectMode(false); setSelectedImages(new Set()); }}
              >
                Cancel
              </button>
            </>
          ) : showSaveMenu ? (
            <>
              <button className="mobile-save-btn" onClick={() => handleMobileSave(true)}>
                Save All
              </button>
              <button className="mobile-save-btn" onClick={() => { setIsSelectMode(true); setShowSaveMenu(false); }}>
                Select
              </button>
              <button className="mobile-save-btn cancel" onClick={() => setShowSaveMenu(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button className="mobile-save-btn primary" onClick={() => setShowSaveMenu(true)}>
              Save or Share
            </button>
          )}
        </div>
      )}

      {downloadRequest && (
        <DownloadDialog
          images={downloadRequest.images}
          onClose={() => setDownloadRequest(null)}
          onReady={runDownload}
        />
      )}
    </div>
  );
}

export default Dashboard;